import { DateTime } from 'luxon';
import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoaderListItem from '../../components/ListItem/LoaderListItem';
import { FORMAT_DATE_DISPLAY_DAY_SHORT, LIST_TASK_GROUP_STATUS, LIST_TASK_STATUS } from '../../const';
import { useValueSelector } from '../../hooks/useValueSelector';
import { RootState } from '../../store';
import { setterTaskBoard } from '../../store/slices/taskBoardSlice';
import { ItemTaskType, ListItemType, LoaderDataType, ScheduleItemType, SubTaskType } from '../../types';
import { TaskBoardColumnsType, TaskBoardColumnType } from '../taskBoardTypes';

export const BACKLOG_ID = 0;

interface TaskBoardColumnsLoaderProps {
    onFinishLoadData?: () => void;
}

export const TaskBoardColumnsLoader: React.FC<TaskBoardColumnsLoaderProps> = ({ onFinishLoadData }) => {
    const dispatch = useDispatch();

    const dataTaskFromServer = useSelector<RootState, LoaderDataType<ItemTaskType>>(
        (state) => state.gantLoader.dataTaskFromServer,
    );

    const itemsFromServer = useValueSelector<RootState, LoaderDataType<ScheduleItemType>>(
        (state) => state.gantLoader.dataScheduleItemFromServer,
    );
    const [scheduledItemsMap, setScheduledItemsMap] = useState<Map<string, ScheduleItemType> | null>(null);

    useEffect(() => {
        if (itemsFromServer.connectionId) {
            setScheduledItemsMap(
                new Map<string, ScheduleItemType>(itemsFromServer.rows.map((item) => [item.id, item])),
            );
        }
    }, [itemsFromServer]);

    const [taskGroups, setTaskGroups] = useState<{
        rows: ListItemType[];
        count: number;
        connectionId: string;
    }>({ rows: [], count: 0, connectionId: '' });

    const [taskStatus, setTaskStatus] = useState<{
        rows: ListItemType[];
        count: number;
        connectionId: string;
    }>({ rows: [], count: 0, connectionId: '' });

    useEffect(() => {
        if (
            !taskStatus ||
            !taskGroups ||
            taskGroups.count === 0 ||
            !dataTaskFromServer.connectionId ||
            !scheduledItemsMap
        ) {
            return;
        }

        const items = itemsFromServer.rows;
        const itemsMap = scheduledItemsMap;

        const backlogColumn: TaskBoardColumnType = {
            name: 'Backlog',
            tasks: [],
            id: BACKLOG_ID,
            dropAreas: [
                {
                    id: BACKLOG_ID,
                    name: 'Backlog',
                },
            ],
        };

        const groups: TaskBoardColumnType[] = taskGroups.rows.map((group) => ({
            name: group.name,
            id: Number(group.id),
            tasks: [],
            dropAreas: [],
        }));

        groups.unshift(backlogColumn);

        const columns: TaskBoardColumnsType = Object.fromEntries(groups.map((column) => [column.id, column]));

        const statusColumnMap = new Map<number, TaskBoardColumnType>();
        const statusNameMap = new Map<number, string>();

        taskStatus.rows.forEach((status) => {
            const column = columns[Number(status.parent)];
            if (column) {
                column.dropAreas.push({
                    name: status.name,
                    id: Number(status.id),
                });

                statusColumnMap.set(column.id, column);
                statusNameMap.set(Number(status.id), status.name);
            }
        });

        const processedTasks = dataTaskFromServer.rows.map((row) => ({
            ...row,
            assignee_for_task: row.assignee_for_task ? (row.assignee_for_task as string)?.split(',') || [] : [],
        }));

        const projectItemsMap = new Map<string | undefined, ScheduleItemType>(
            items.filter((item) => item.role_item === 'project').map((item) => [item.project_id, item]),
        );

        processedTasks.forEach((task) => {
            const column = task.status ? statusColumnMap.get(task.status) : backlogColumn;

            if (column) {
                const scheduleItem = task.schedule_item_id ? itemsMap.get(task.schedule_item_id) : null;
                const project = task.project_id ? projectItemsMap.get(task.project_id) : null;

                const subTaskList = task.subtask ? JSON.parse(task.subtask) : [];
                const totalSubTask = subTaskList.length;
                const doneSubTask = subTaskList.filter((subTask: SubTaskType) => subTask.status).length;

                const startDatetime = `${DateTime.fromSQL(task.datetime).toFormat(FORMAT_DATE_DISPLAY_DAY_SHORT)} ${task.start_format_datetime}`;

                task.datetime = DateTime.fromSQL(task.datetime).toFormat('yyyy-MM-dd');

                column.tasks.push({
                    columnId: column.id,
                    name: task.name,
                    id: task.id,
                    startDatetime,
                    scheduleItemId: scheduleItem?.id,
                    scheduleItemName: scheduleItem?.name,
                    scheduleItemColor: scheduleItem?.color,
                    projectName: project?.name,
                    subtaskProgress: `${doneSubTask}/${totalSubTask}`,
                    priority: task.priority,
                    status: task.sub_status ? statusNameMap.get(task.sub_status) : '',
                    type: task.type,
                    assignee: task.owner_task_id,
                    dataFromServer: task,
                });
            }
        });

        if (onFinishLoadData) {
            onFinishLoadData();
        }

        dispatch(
            setterTaskBoard({
                columns,
            }),
        );
    }, [taskGroups, taskStatus, dataTaskFromServer, scheduledItemsMap]);

    return (
        <Suspense>
            <LoaderListItem
                onUpdateData={(rows: ListItemType[], count: number, connectionId: string) => {
                    setTaskGroups({ rows, count, connectionId });
                }}
                varRequest={{ type: LIST_TASK_GROUP_STATUS }}
            />

            <LoaderListItem
                onUpdateData={(rows: ListItemType[], count: number, connectionId: string) => {
                    setTaskStatus({ rows, count, connectionId });
                }}
                varRequest={{ type: LIST_TASK_STATUS }}
            />
        </Suspense>
    );
};
